import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { useKillSplineLogo } from '../../hooks/useKillSplineLogo';
import { getSplineSceneUrl, shouldEnableSpline } from '../../config/splineScenes';
import DropdownNavigation from './dropdown-navigation';
import { 
  Menu, 
  X, 
  ChevronRight, 
  Home, 
  Briefcase, 
  Users, 
  Wrench, 
  MessageSquare, 
  Globe,
  Sparkles,
  Rocket,
  Brain,
  Building2,
  TrendingUp,
  Zap
} from 'lucide-react';

/**
 * PageHero Component
 * 
 * A reusable hero section component that displays a page name with a 3D Spline background.
 * Automatically handles logo blocking and script loading. Supports different Spline scenes.
 * 
 * @param {Object} props - Component props
 * @param {string} props.pageName - The name of the page to display (required)
 * @param {string} [props.subtitle] - Optional subtitle text
 * @param {string} [props.splineSceneUrl] - Custom Spline scene URL (optional)
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.showDecorations=true] - Whether to show decorative elements
 * @param {string} [props.size="large"] - Size variant: "small", "medium", "large", "xl"
 * @param {boolean} [props.fullHeight=true] - Whether to use full height (50vh) or compact height (30vh)
 * @param {boolean} [props.enableSpline=true] - Whether to show Spline background
 * 
 * @example
 * // Basic usage
 * <PageHero pageName="About Us" />
 * 
 * @example
 * // With custom Spline scene
 * <PageHero 
 *   pageName="Technology & Expertise" 
 *   subtitle="Our cutting-edge tech stack and capabilities"
 *   splineSceneUrl="https://prod.spline.design/your-custom-scene.splinecode"
 *   size="xl"
 * />
 * 
 * @example
 * // Without Spline background
 * <PageHero 
 *   pageName="Contact" 
 *   size="small"
 *   fullHeight={false}
 *   enableSpline={false}
 * />
 */
const PageHero = ({ 
  pageName, 
  subtitle, 
  splineSceneUrl,
  className = "", 
  showDecorations = true,
  size = "large",
  fullHeight = true,
  enableSpline = true,
  ctaButtons = null
}) => {
  const { t, language, changeLanguage } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  // Navigation structure for mobile menu
  const navigationData = [
    {
      title: t('nav.home'),
      href: '/',
      icon: Home
    },
    {
      title: t('nav.services'),
      href: '/services',
      icon: Wrench,
      submenu: [
        { title: 'Web Development', href: '/services/web-development', icon: Rocket },
        { title: 'AI Solutions', href: '/services/ai-solutions', icon: Brain },
        { title: 'CRM Systems', href: '/services/crm-systems', icon: Building2 },
        { title: 'Digital Marketing', href: '/services/digital-marketing', icon: TrendingUp },
        { title: 'SaaS Development', href: '/services/saas-development', icon: Zap }
      ]
    },
    {
      title: t('nav.solutions'),
      href: '/solutions',
      icon: Sparkles,
      submenu: [
        { title: 'Business Solutions', href: '/solutions/business', icon: Briefcase },
        { title: 'Tech Solutions', href: '/solutions/tech', icon: Wrench },
        { title: 'Team Solutions', href: '/solutions/team', icon: Users }
      ]
    },
    {
      title: t('nav.about'),
      href: '/about',
      icon: Users
    },
    {
      title: t('nav.contact'),
      href: '/contact',
      icon: MessageSquare
    }
  ];
  
  // Get Spline scene URL from configuration or use provided URL
  const configuredSplineUrl = getSplineSceneUrl(pageName);
  const currentSplineUrl = splineSceneUrl || configuredSplineUrl;
  
  // Determine if Spline should be enabled
  const splineEnabled = enableSpline && shouldEnableSpline(pageName) && currentSplineUrl;
  
  // Use the same logo blocker logic
  useKillSplineLogo();

  // Load Spline 3D script (only if Spline is enabled)
  useEffect(() => {
    if (splineEnabled && !document.querySelector('script[src*="spline-viewer"]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://unpkg.com/@splinetool/viewer@1.10.57/build/spline-viewer.js';
      (document.head || document.body).appendChild(script);
    }
  }, [splineEnabled]);

  // Validate required props after hooks
  if (!pageName) {
    console.warn('PageHero: pageName prop is required');
    return null;
  }

  // Size variants
  const sizeClasses = {
    small: "text-3xl md:text-4xl",
    medium: "text-4xl md:text-5xl", 
    large: "text-6xl md:text-8xl",
    xl: "text-7xl md:text-9xl"
  };

  const subtitleSizeClasses = {
    small: "text-lg md:text-xl",
    medium: "text-lg md:text-xl",
    large: "text-xl md:text-2xl", 
    xl: "text-xl md:text-2xl"
  };

  const heightClass = fullHeight ? "h-[50vh]" : "h-[30vh]";

  return (
    <div className={`bg-background ${language === 'ar' ? 'rtl' : 'ltr'}`} style={{ fontFamily: language === 'ar' ? "'Cairo', Arial, sans-serif" : "inherit" }}>
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center justify-between h-16 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>

            {/* Logo - Hidden on mobile */}
            <div className="hidden md:flex items-center">
              <img
                src="/Logos/Asset 1front.svg"
                alt="Envael Logo" 
                className="w-8 h-8"
              />
            </div>

            {/* Navigation Links */}
            <div className={`hidden md:flex items-center ${language === 'ar' ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
              <a href="/" className="text-muted-foreground hover:text-foreground transition-colors">{t('nav.home')}</a>
              <DropdownNavigation />
            </div>

            {/* CTA Buttons */}
            <div className={`flex items-center space-x-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              {/* Language Dropdown - Hidden on mobile */}
              <div className="relative hidden md:block">
                <button 
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  onClick={() => {
                    const dropdown = document.getElementById('language-dropdown');
                    dropdown.classList.toggle('hidden');
                  }}
                >
                  <Globe className="w-4 h-4" />
                  <span>{language.toUpperCase()}</span>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div 
                  id="language-dropdown"
                  className={`hidden absolute top-full mt-2 w-48 bg-background border border-border rounded-lg shadow-lg z-50 ${language === 'ar' ? 'left-0' : 'right-0'}`}
                >
                  <div className="py-2">
                    {['en', 'ar', 'fr', 'es', 'de', 'it'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => changeLanguage(lang)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-accent transition-colors ${
                          language === lang ? 'text-primary bg-accent/50' : 'text-foreground'
                        }`}
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA Button - Hidden on mobile */}
              <a 
                href="/contact" 
                className="hidden md:block bg-primary text-primary-foreground px-6 py-2 rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Full-Screen Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-background/90 backdrop-blur-md animate-in fade-in-0 duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Content */}
          <div className="relative h-full flex flex-col animate-in slide-in-from-left-4 duration-300 ease-out">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center space-x-3">
                <img
                  src="/Logos/Asset 1front.svg"
                  alt="Envael Logo" 
                  className="w-8 h-8"
                />
                <span className="text-lg font-bold text-foreground">Envael</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Navigation Items */}
            <div className="flex-1 overflow-y-auto py-6">
              <div className="px-4 space-y-2">
                {navigationData.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between">
                        <a
                          href={item.href}
                          className="flex items-center space-x-3 px-4 py-3 rounded-lg text-foreground hover:bg-accent transition-all duration-200 hover:translate-x-1 w-full group"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <IconComponent className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                          <span className="font-medium">{item.title}</span>
                        </a>
                        {item.submenu && (
                          <button
                            onClick={() => setActiveSubmenu(activeSubmenu === index ? null : index)}
                            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200 hover:scale-110"
                          >
                            <ChevronRight 
                              className={`h-4 w-4 transition-transform duration-200 ${
                                activeSubmenu === index ? 'rotate-90' : ''
                              }`} 
                            />
                          </button>
                        )}
                      </div>
                      
                      {/* Submenu */}
                      {item.submenu && activeSubmenu === index && (
                        <div className="ml-8 mt-2 space-y-1 animate-in slide-in-from-left-2 duration-200 ease-out">
                          {item.submenu.map((subItem, subIndex) => {
                            const SubIconComponent = subItem.icon;
                            return (
                              <a
                                key={subIndex}
                                href={subItem.href}
                                className="flex items-center space-x-3 px-4 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-200 hover:translate-x-1"
                                onClick={() => setIsMobileMenuOpen(false)}
                                style={{ animationDelay: `${subIndex * 50}ms` }}
                              >
                                <SubIconComponent className="h-4 w-4" />
                                <span>{subItem.title}</span>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border p-4">
              <div className="space-y-4">
                {/* Language Selection */}
                <div>
                  <h3 className="text-sm font-medium text-foreground mb-2">Language</h3>
                  <div className="flex flex-wrap gap-2">
                    {['en', 'ar', 'fr', 'es', 'de', 'it'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          changeLanguage(lang);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          language === lang 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'
                        }`}
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <a
                  href="/contact"
                  className="block w-full bg-primary text-primary-foreground text-center py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Get Started
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className={`relative ${heightClass} flex items-center justify-center overflow-hidden ${className}`} style={{ minHeight: '50vh' }}>
        {/* Spline 3D Interactive Background - Customizable */}
        {splineEnabled && (
          <div 
            id="spline-background" 
            className="absolute inset-0 z-0 flex items-center justify-center"
            style={{ width: '100%', height: '100%' }}
          >
            <spline-viewer 
              url={currentSplineUrl}
              style={{ width: '100%', height: '100%', minHeight: '50vh' }}
            ></spline-viewer>
          </div>
        )}

        {/* Page Name Content - Centered */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8">
          <div className={`max-w-4xl mx-auto ${language === 'ar' ? 'text-right' : 'text-center'}`}>
            {/* Page Name - Large and Stylish */}
            <h1 className={`${sizeClasses[size]} font-bold text-foreground leading-tight mb-6`}>
              <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent animate-pulse">
                {pageName}
              </span>
            </h1>
            
            {/* Subtitle - Optional */}
            {subtitle && (
              <p className={`${subtitleSizeClasses[size]} text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto`}>
                {subtitle}
              </p>
            )}

            {/* CTA Buttons - Optional */}
            {ctaButtons && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <a href={ctaButtons.primary?.action || "#"} className="px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold text-lg group">
                  {ctaButtons.primary?.text || ctaButtons.primary}
                  <svg className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
                <a href={ctaButtons.secondary?.action || "#"} className="px-8 py-4 border-2 border-primary/20 text-primary rounded-lg hover:bg-primary/5 transition-colors font-semibold text-lg">
                  {ctaButtons.secondary?.text || ctaButtons.secondary}
                </a>
                <a href={ctaButtons.tertiary?.action || "#"} className="px-8 py-4 text-primary hover:bg-primary/10 transition-colors font-semibold text-lg">
                  {ctaButtons.tertiary?.text || ctaButtons.tertiary}
                </a>
              </div>
            )}
            
            {/* Decorative Elements - Optional */}
            {showDecorations && (
              <div className="flex items-center justify-center space-x-4 mt-8">
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
                <div className="w-2 h-2 bg-primary rounded-full animate-ping"></div>
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
              </div>
            )}
          </div>
        </div>

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-background/20 backdrop-blur-[1px] z-5"></div>
      </section>
    </div>
  );
};

export default PageHero;
